/**
 * Hook: useContractTemplates
 * Descrição: CRUD completo de modelos de contrato via RPCs server-side
 *
 * RELEASE: v2.0.0 - 2026-05-10
 * - Substitui SELECT/INSERT/UPDATE/DELETE diretos por RPCs:
 *   get_contract_templates_list, create_contract_template,
 *   update_contract_template, delete_contract_template
 * - Padrão de abstração server-side (sem acesso direto à tabela)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';
import type { ContractTemplate, ContractTemplateInput } from '@/types/contracts';

// Logger do módulo
const logger = createLogger('ContractTemplates');

/**
 * Hook principal para gerenciamento de templates de contrato
 */
export function useContractTemplates() {
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // =========================================================================
  // Query: Listar todos os templates do tenant via RPC
  // =========================================================================
  const {
    data: templates = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['contract-templates', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug('Buscando templates de contrato (RPC)', { tenantId });

      const { data, error } = await supabase.rpc('get_contract_templates_list', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar templates', { error });
        throw error;
      }

      // Mapeia latest_version_number para a forma { contract_template_versions: [{version_number}] }
      // que a UI já consome (mantém compatibilidade)
      const rows = (data || []).map((t: any) => ({
        ...t,
        contract_template_versions: t.latest_version_number != null
          ? [{ version_number: t.latest_version_number }]
          : [],
      }));

      logger.info('Templates carregados', { count: rows.length });
      return rows as unknown as ContractTemplate[];
    },
    enabled: !!tenantId,
  });

  // =========================================================================
  // Mutation: Criar novo template via RPC
  // =========================================================================
  const createTemplate = useMutation({
    mutationFn: async (input: ContractTemplateInput) => {
      if (!tenantId) throw new Error('Tenant não identificado');

      logger.info('Criando template de contrato (RPC)', { name: input.name });

      // Payload JSONB para a RPC — apenas campos definidos pelo usuário
      const payload: Record<string, unknown> = {
        name: input.name,
        description: input.description ?? null,
        content_html: input.content_html,
        variables: input.variables ?? [],
        category: input.category ?? 'geral',
        is_active: input.is_active !== false,
        allowed_delivery_methods: input.allowed_delivery_methods ?? ['email'],
        require_selfie: input.require_selfie ?? false,
        background_image_path: input.background_image_path ?? null,
        show_page_numbers: input.show_page_numbers ?? false,
        page_number_position: input.page_number_position ?? 'bottom-right',
        base_pdf_path: input.base_pdf_path ?? null,
        cover_pdf_path: input.cover_pdf_path ?? null,
        base_pdf_position: input.base_pdf_position ?? 'before',
        bg_margin_top_mm: input.bg_margin_top_mm ?? 33,
        bg_margin_bottom_mm: input.bg_margin_bottom_mm ?? 20,
        pagination_y_offset_mm: input.pagination_y_offset_mm ?? 12,
        // v2026-05: tipo de origem do modelo (editor HTML vs PDF pronto)
        source_type: input.source_type ?? 'html',
        source_pdf_storage_path: input.source_pdf_storage_path ?? null,
      };


      const { data, error } = await supabase.rpc('create_contract_template', {
        p_payload: payload as any,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao criar template', { error });
        throw error;
      }

      return { id: data } as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates', tenantId] });
      toast({
        title: 'Modelo criado',
        description: 'O modelo de contrato foi criado com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha ao criar template', { error: error.message });
      toast({
        title: 'Erro ao criar modelo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // =========================================================================
  // Mutation: Atualizar template existente via RPC
  // =========================================================================
  const updateTemplate = useMutation({
    mutationFn: async ({ id, ...input }: ContractTemplateInput & { id: string }) => {
      if (!tenantId) throw new Error('Tenant não identificado');

      logger.info('Atualizando template (RPC)', { id });

      // Monta payload apenas com campos efetivamente passados (parcial)
      const payload: Record<string, unknown> = {};
      const keys: (keyof ContractTemplateInput)[] = [
        'name','description','content_html','variables','category','is_active',
        'allowed_delivery_methods','require_selfie','background_image_path',
        'show_page_numbers','page_number_position','base_pdf_path','cover_pdf_path',
        'base_pdf_position','bg_margin_top_mm','bg_margin_bottom_mm','pagination_y_offset_mm',
        // v2026-05: novos campos para modelo baseado em PDF pronto
        'source_type','source_pdf_storage_path',
      ];

      for (const k of keys) {
        if ((input as any)[k] !== undefined) payload[k as string] = (input as any)[k];
      }

      const { error } = await supabase.rpc('update_contract_template', {
        p_id: id,
        p_payload: payload as any,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao atualizar template', { error });
        throw error;
      }

      return { id } as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates', tenantId] });
      toast({
        title: 'Modelo atualizado',
        description: 'O modelo de contrato foi atualizado com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha ao atualizar template', { error: error.message });
      toast({
        title: 'Erro ao atualizar modelo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // =========================================================================
  // Mutation: Deletar template via RPC
  // =========================================================================
  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant não identificado');

      logger.info('Deletando template (RPC)', { id });

      const { error } = await supabase.rpc('delete_contract_template', {
        p_id: id,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao deletar template', { error });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates', tenantId] });
      toast({
        title: 'Modelo excluído',
        description: 'O modelo de contrato foi excluído com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha ao deletar template', { error: error.message });
      toast({
        title: 'Erro ao excluir modelo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    templates,
    isLoading,
    error,
    refetch,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}
